/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
 *
 * Note that this file is required for the build process.
 */

// Dependency libs
import React from 'react';
import ReactDOMClient from 'react-dom/client';
// Import default styles before other CSS-related modules are imported
// This ensures that the styles in marketplaceDefaults.css are included
// as first ones in the final build CSS build file.
import './styles/marketplaceDefaults.css';
import './styles/customMediaQueries.css';
import './styles/propertySets.css';
import './styles/baseFonts.css';
import './styles/premiumDesignSystem.css';
import defaultConfig from './config/configDefault';
import appSettings from './config/settings';
import { LoggingAnalyticsHandler, GoogleAnalyticsHandler } from './analytics/handlers';
import configureStore from './store';

// Utils
import { mergeConfig } from './util/configHelpers';
import { matchPathname } from './util/routes';
import * as apiUtils from './util/api';
import * as log from './util/log';
import { installRequestTiming } from './util/requestTiming';
import { createInstance, types as sdkTypes } from './util/sdkLoader';
import { loadableReady } from '@loadable/component';

// Import relevant global duck files
import { authInfo } from './ducks/auth.duck';
import { fetchAppAssets } from './ducks/hostedAssets.duck';
import { fetchCurrentUser } from './ducks/user.duck';
import { syncProfileFromSharetribe } from './ducks/supabaseProfile.duck';

// Route config
import routeConfiguration from './routing/routeConfiguration';
// App it self
import { ClientApp, renderApp } from './app';

const BOOT_OVERLAY_ID = 'riticket-boot-overlay';
const BOOT_SPINNER_ID = 'riticket-boot-spinner';

const showBootOverlay = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const existing = document.getElementById(BOOT_OVERLAY_ID);
  if (existing) {
    return existing;
  }

  const overlay = document.createElement('div');
  overlay.id = BOOT_OVERLAY_ID;
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.zIndex = '9999';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';
  overlay.style.background = '#000000';
  const spinner = document.createElement('div');
  spinner.id = BOOT_SPINNER_ID;
  spinner.setAttribute('aria-label', 'Loading');
  spinner.style.width = '56px';
  spinner.style.height = '56px';
  spinner.style.borderRadius = '999px';
  spinner.style.border = '4px solid rgba(93, 43, 192, 0.16)';
  spinner.style.borderTopColor = 'var(--marketplaceColor, #5d2bc0)';
  spinner.style.animation = 'riticketBootSpin 0.8s linear infinite';

  const styleTag = document.createElement('style');
  styleTag.textContent = `
    @keyframes riticketBootSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  overlay.appendChild(styleTag);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);
  return overlay;
};

const hideBootOverlay = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const existing = document.getElementById(BOOT_OVERLAY_ID);
  if (existing?.parentNode) {
    existing.parentNode.removeChild(existing);
  }
};

const render = (store, shouldHydrate) => {
  // If the server already loaded the auth information, render the app
  // immediately. Otherwise wait for the flag to be loaded and render
  // when auth information is present.
  const state = store.getState();
  const cdnAssetsVersion = state.hostedAssets.version;
  const authInfoLoaded = state.auth.authInfoLoaded;
  const info = authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo());
  showBootOverlay();

  const mountClientApp = fetchedAppAssets => {
    const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
    // We'll handle translations as a separate data.
    // It's given to React Intl instead of pushing to config Context
    const translations = translationsRaw?.data || {};

    // Rest of the assets are considered as hosted configs
    const configEntries = Object.entries(rest);
    const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
      return { ...collectedData, [name]: content.data || {} };
    }, {});

    if (shouldHydrate) {
      const container = document.getElementById('root');

      ReactDOMClient.hydrateRoot(
        container,
        <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />,
        { onRecoverableError: log.onRecoverableError }
      );
    } else {
      const container = document.getElementById('root');
      const root = ReactDOMClient.createRoot(container);
      root.render(
        <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />
      );
    }
  };

  info
    .then(() => {
      // Ensure that Loadable Components is ready only when hydrating SSR markup,
      // and fetch hosted assets before initializing the client shell.
      const loadableReadyPromise = shouldHydrate ? loadableReady() : Promise.resolve();
      return Promise.all([
        loadableReadyPromise,
        store.dispatch(fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)),
      ]);
    })
    .then(([_, fetchedAppAssets]) => {
      const { isAuthenticated } = store.getState().auth || {};
      const currentUserPromise = isAuthenticated
        ? store.dispatch(fetchCurrentUser({ enforce: true })).catch(e => {
            log.error(e, 'initial-fetch-current-user-failed');
          })
        : Promise.resolve();

      return currentUserPromise.then(() => {
        const { isAuthenticated: authenticatedNow } = store.getState().auth || {};
        const syncSupabaseProfilePromise = authenticatedNow
          ? store.dispatch(syncProfileFromSharetribe()).catch(e => {
              log.error(e, 'initial-sync-supabase-profile-failed');
            })
          : Promise.resolve();

        mountClientApp(fetchedAppAssets);
        hideBootOverlay();

        if (!isAuthenticated) {
          return store.dispatch(fetchCurrentUser()).catch(e => {
            log.error(e, 'initial-fetch-current-user-failed');
          });
        }

        return syncSupabaseProfilePromise;
      });
    })
    .catch(e => {
      hideBootOverlay();
      log.error(e, 'browser-side-render-failed');
    });
};

const setupAnalyticsHandlers = googleAnalyticsId => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  // Add Google Analytics 4 (GA4) handler if tracker ID is found
  if (googleAnalyticsId) {
    if (googleAnalyticsId.indexOf('G-') !== 0) {
      console.warn(
        'Google Analytics 4 (GA4) should have measurement id that starts with "G-" prefix'
      );
    } else {
      handlers.push(new GoogleAnalyticsHandler());
    }
  }

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  installRequestTiming();

  // set up logger with Sentry DSN client key and environment
  log.setup();

  if (appSettings.enableSentryTest) {
    const sentryTest = new URLSearchParams(window.location.search).get('sentryTest');
    if (sentryTest === 'frontend') {
      log.captureException(
        new Error(`Riticket frontend Sentry test (${appSettings.env || 'unknown'})`),
        'sentry-test-frontend',
        {
          environment: appSettings.env,
          path: window.location.pathname,
        }
      );
    }
  }

  const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
  const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
    ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
    : {};

  // eslint-disable-next-line no-underscore-dangle
  const preloadedState = window.__PRELOADED_STATE__ || '{}';
  const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
  const sdk = createInstance({
    transitVerbose: appSettings.sdk.transitVerbose,
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    typeHandlers: apiUtils.typeHandlers,
    ...baseUrl,
    ...assetCdnBaseUrl,
  });

  // Note: on localhost:3000, you need to use environment variable.
  const googleAnalyticsIdFromSSR = initialState?.hostedAssets?.googleAnalyticsId;
  const googleAnalyticsId = googleAnalyticsIdFromSSR || process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
  const analyticsHandlers = setupAnalyticsHandlers(googleAnalyticsId);
  const store = configureStore(initialState, sdk, analyticsHandlers);

  require('./util/polyfills');
  render(store, !!window.__PRELOADED_STATE__);

  if (appSettings.dev) {
    // Expose stuff for the browser REPL
    window.app = {
      appSettings,
      defaultConfig,
      sdk,
      sdkTypes,
      store,
    };
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export {
  matchPathname,
  configureStore,
  routeConfiguration,
  defaultConfig,
  mergeConfig,
  fetchAppAssets,
};
